import React, { useState } from 'react';
import { FileText, Download, X, Loader2, CheckCircle } from 'lucide-react';
import { downloadBookPDF, PdfExportOptions } from '../../utils/pdfGenerator';
import { TOTAL_PAGES, getActs } from '../../data/tome';

export interface PdfExportModalProps {
  isOpen: boolean;
  currentPage: number;
  onClose: () => void;
}

export const PdfExportModal: React.FC<PdfExportModalProps> = ({
  isOpen,
  currentPage,
  onClose,
}) => {
  const [rangeMode, setRangeMode] = useState<'sample' | 'current_act' | 'custom' | 'full'>('sample');
  const [customStart, setCustomStart] = useState(1);
  const [customEnd, setCustomEnd] = useState(100);
  const [isExporting, setIsExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusText, setStatusText] = useState('');
  const [completed, setCompleted] = useState(false);

  if (!isOpen) return null;

  const acts = getActs();
  const currentAct = acts.find((a) => currentPage >= a.startPage && currentPage <= a.endPage) || acts[0];

  const handleExport = async () => {
    setIsExporting(true);
    setProgress(0);
    setCompleted(false);

    let start = 1;
    let end = 50;

    if (rangeMode === 'sample') {
      start = 1;
      end = 50;
    } else if (rangeMode === 'current_act') {
      start = currentAct.startPage;
      end = currentAct.endPage;
    } else if (rangeMode === 'full') {
      start = 1;
      end = TOTAL_PAGES;
    } else {
      start = Math.max(1, Math.min(TOTAL_PAGES, customStart));
      end = Math.max(start, Math.min(TOTAL_PAGES, customEnd));
    }

    const options: PdfExportOptions = {
      startPage: start,
      endPage: end,
      includeCover: true,
      includeToc: true,
      onProgress: (pct, msg) => {
        setProgress(pct);
        setStatusText(msg);
      },
    };

    try {
      const fileName =
        rangeMode === 'current_act'
          ? `The_Caesura_Engine_Act_${currentAct.actNumber}.pdf`
          : rangeMode === 'full'
          ? `The_Caesura_Engine_Complete_1000_Pages.pdf`
          : `The_Caesura_Engine_Pages_${start}_to_${end}.pdf`;

      await downloadBookPDF(options, fileName);
      setCompleted(true);
    } catch (err) {
      console.error('[PdfExportModal] Export failed:', err);
      setStatusText('Export failed. Check console for details.');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose} role="dialog" aria-modal="true">
      <div className="modal-card pdf-export-modal-card" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-group">
            <FileText size={20} className="modal-icon-accent" />
            <h2 className="modal-title">Export Collector's Edition PDF</h2>
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Close Export Modal">
            <X size={20} />
          </button>
        </div>

        <div className="modal-body-content">
          <p className="pdf-modal-desc">
            Generate a publication-grade, A4 book-formatted PDF edition of "The Caesura Engine" complete with void-black cover art, diagnostic rubrics, Table of Acts, and Marcus Ward's engineering logs.
          </p>

          <section className="settings-section">
            <h3 className="section-title">Select Volume Range</h3>
            <div className="options-grid">
              <button
                type="button"
                className={`option-btn ${rangeMode === 'sample' ? 'active' : ''}`}
                onClick={() => setRangeMode('sample')}
              >
                <span className="btn-option-name">Executive Highlights</span>
                <span className="btn-option-sub">Pages 1 – 50 (Instant compilation)</span>
              </button>

              <button
                type="button"
                className={`option-btn ${rangeMode === 'current_act' ? 'active' : ''}`}
                onClick={() => setRangeMode('current_act')}
              >
                <span className="btn-option-name">Current Act ({currentAct.title})</span>
                <span className="btn-option-sub">Pages {currentAct.startPage} – {currentAct.endPage}</span>
              </button>

              <button
                type="button"
                className={`option-btn ${rangeMode === 'full' ? 'active' : ''}`}
                onClick={() => setRangeMode('full')}
              >
                <span className="btn-option-name">Full 1,000-Page Tome</span>
                <span className="btn-option-sub">Complete unabridged manuscript</span>
              </button>

              <button
                type="button"
                className={`option-btn ${rangeMode === 'custom' ? 'active' : ''}`}
                onClick={() => setRangeMode('custom')}
              >
                <span className="btn-option-name">Custom Page Range</span>
                <span className="btn-option-sub">Specify exact page bounds</span>
              </button>
            </div>

            {rangeMode === 'custom' && (
              <div className="custom-range-inputs">
                <label>
                  Start Page:
                  <input
                    type="number"
                    min={1}
                    max={TOTAL_PAGES}
                    value={customStart}
                    onChange={(e) => setCustomStart(parseInt(e.target.value, 10) || 1)}
                  />
                </label>
                <label>
                  End Page:
                  <input
                    type="number"
                    min={1}
                    max={TOTAL_PAGES}
                    value={customEnd}
                    onChange={(e) => setCustomEnd(parseInt(e.target.value, 10) || 100)}
                  />
                </label>
              </div>
            )}
          </section>

          {/* Export Progress Bar */}
          {isExporting && (
            <div className="export-progress-wrap">
              <div className="progress-info-row">
                <span className="progress-status-msg">{statusText}</span>
                <span className="progress-pct-val">{progress}%</span>
              </div>
              <div className="progress-track">
                <div className="progress-fill-bar" style={{ width: `${progress}%` }} />
              </div>
            </div>
          )}

          {completed && (
            <div className="export-success-badge">
              <CheckCircle size={18} color="var(--planck-cyan)" />
              <span>PDF generated and downloaded to your device!</span>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={isExporting}>
            Cancel
          </button>
          <button
            className="btn-primary-done export-download-btn"
            onClick={handleExport}
            disabled={isExporting}
          >
            {isExporting ? (
              <>
                <Loader2 size={16} className="generating-spinner" />
                <span>Compiling Book...</span>
              </>
            ) : (
              <>
                <Download size={16} />
                <span>Compile & Download PDF</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
